"use client";

// useFormRenderer (spec §8): renders a form builder form and sends it. This file is the thin React layer; the rules are in
// controller.js (state, conditions, repeat groups, submit flow) and the fields, which are tested without React or a DOM.
//
//   const form = useFormRenderer({ form, styles, custom_fields, extra_meta, on_login_required, on_success, on_error });
//   form.render()   // title, description, groups, fields and the submit button
//   form.values, form.errors, form.submitting
//
// `form` is what getFormDefinition returned (from a server component); or pass `form_id` / `form_key` and the hook loads it.
import { createElement as h, useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import { currency_symbols } from "@core/helpers/client/components/price";
import useCapToken from "@core/helpers/client/hooks/useCapToken";
import CapCaptcha from "@core/helpers/client/components/cap-captcha";
import { get_form_definition_query } from "@core/services/form-definitions";
import { create_form_submission_mutation } from "@core/services/form-submissions";
import { upload_form_files } from "@core/services/form-submissions/upload";
import { LIMITS } from "./form_helper.js";
import { get_iranian_states_and_cities_query } from "@core/services/addresses";
import default_class_names from "./form-renderer.module.css";
import { create_form_controller } from "./controller.js";
import { FormView } from "./FormView.js";
import { Group } from "./Group.js";
import { groups_of, fields_of } from "./form_logic.js";
import { GroupInstance } from "./Group.js";
import { group_defaults } from "./form_logic.js";
import { scope_props, STABLE_FLAG } from "./styles.js";
import { dom_id } from "./fields/shell.js";
import { normalize_geo } from "./fields/geo.js";
import { label_of, lang_of, first_error_text } from "./messages.js";
import { FORM_PLACE } from "./constants.js";

const has_geo_fields = (definition) => groups_of(definition?.version).some((group) => fields_of(group).some((field) => field.type === "state" || field.type === "city"));

const NO_CLASSES = Object.freeze({});

// what the hook reads before there is a definition to render
const EMPTY_SNAPSHOT = Object.freeze({ values: {}, errors: {}, submitting: false, submitted: false, duplicate: false, form_state: null });
const no_subscribe = () => () => {};
const empty_snapshot = () => EMPTY_SNAPSHOT;

const useFormRenderer = (options = {}) => {
    const {
        form: given_form, form_id: wanted_id, form_key: wanted_key, styles, custom_fields, extra_meta, messages, lang: given_lang,
        unstyled = false, custom_rules, field_components, submission_id, scroll_to_error = false, stable_class_names = false, price_unit: given_price_unit, class_names: given_class_names, submit_label, success_content, title_tag, hide_title,
        on_login_required, on_success, on_error, captcha = true, max_file_bytes: given_max_file_bytes,
    } = options;
    // upload_files: true sends the chosen files to the web-server as files when the visitor sends the form (with a solved captcha
    // of their own), and the answer names them - never base64 inside the answer. Added for jument; off by default.
    const { upload_files = false } = options;
    // uploaded files may be as large as the server takes (5 MB); inside the answer they stay within the JSON's room (3.5 MB)
    const max_file_bytes = given_max_file_bytes ?? (upload_files ? LIMITS.FILE_MAX_BYTES : undefined);

    const auth = useAuthContext();
    const is_logged_in = !!auth?.is_auth;
    const { captcha_enabled: site_has_captcha, get_captcha_token } = useCapToken();
    const dom_form_id = useId();
    // the unit shown after a price: by default (or true) the symbol of the currency saved on the site's web project, a text
    // given instead of it, or none with false
    const site_currency = useWebSiteContext()?.web_site_setting?.web_site_currency;
    const price_unit = typeof given_price_unit === "string" ? given_price_unit : (given_price_unit === false ? undefined : currency_symbols[site_currency]);

    // ---- the definition: given, or loaded by id / key ----
    const [loaded, set_loaded] = useState({ definition: null, loading: false, not_found: false });
    useEffect(() => {
        if (given_form || (!wanted_id && !wanted_key)) {
            return undefined;
        }
        let cancelled = false;
        set_loaded({ definition: null, loading: true, not_found: false });
        get_form_definition_query(wanted_id ? { id: wanted_id } : { key: wanted_key }).then((result) => {
            if (!cancelled) {
                set_loaded(result?.status && result.data ? { definition: result.data, loading: false, not_found: false } : { definition: null, loading: false, not_found: true });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [given_form, wanted_id, wanted_key]);
    const definition = given_form || loaded.definition;

    // ---- the callbacks are read at call time, so a new function each render does not rebuild the controller ----
    const callbacks = useRef({});
    callbacks.current = { custom_rules, submission_id, is_logged_in, on_login_required, on_success, on_error, get_captcha_token, site_has_captcha, captcha };

    // the controller (and the visitor's values) lives as long as it is the same form and version - a parent that builds the
    // `form` object again on every render must not wipe what was typed
    const definition_key = definition?.version ? `${definition.id || definition.key}:${definition.version.version}` : null;
    const controller = useMemo(() => {
        if (!definition?.version) {
            return null;
        }
        return create_form_controller({
            definition,
            extra_meta,
            max_file_bytes,
            is_logged_in: () => callbacks.current.is_logged_in,
            on_login_required: () => callbacks.current.on_login_required?.(),
            on_success: (info) => callbacks.current.on_success?.(info),
            on_error: (info) => callbacks.current.on_error?.(info),
            get_custom_rules: () => callbacks.current.custom_rules,
            get_submission_id: () => callbacks.current.submission_id,
            get_captcha: async () => (callbacks.current.captcha && callbacks.current.site_has_captcha ? callbacks.current.get_captcha_token({ place: FORM_PLACE }) : undefined),
            submit: (input) => create_form_submission_mutation({ input }),
            upload_files: upload_files
                ? (args) => upload_form_files({ ...(definition.id ? { form_definition_id: definition.id } : { form_key: definition.key }), version: definition.version?.version, ...args })
                : undefined,
        });
        // another form or version is a new controller; extra_meta is kept in sync below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definition_key]);

    const snapshot = useSyncExternalStore(
        controller ? controller.subscribe : no_subscribe,
        controller ? controller.get_state : empty_snapshot,
        controller ? controller.get_state : empty_snapshot,
    );

    const extra_meta_key = JSON.stringify(extra_meta ?? null);
    useEffect(() => {
        controller?.set_extra_meta(extra_meta);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controller, extra_meta_key]);

    // ---- states and cities, only when the form has such a field ----
    const [geo, set_geo] = useState(null);
    const wants_geo = has_geo_fields(definition);
    useEffect(() => {
        if (!wants_geo || geo) {
            return undefined;
        }
        let cancelled = false;
        get_iranian_states_and_cities_query().then((result) => {
            if (!cancelled && result?.status) {
                const normalized = normalize_geo({ states: result.data?.states?.value, cities: result.data?.cities?.value });
                set_geo(normalized);
                controller?.set_geo(normalized);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [wants_geo, geo, controller]);

    // ---- the first field with an error takes the focus after a failed submit ----
    useEffect(() => {
        if (!snapshot.focus_path || typeof document === "undefined") {
            return;
        }
        const id = dom_id(dom_form_id, snapshot.focus_path);
        const target = document.getElementById(id) || document.querySelector(`[id^="${id}"]`);
        if (target && typeof target.focus === "function") {
            if (scroll_to_error && typeof target.scrollIntoView === "function") {
                // the first fault comes to the middle of the screen, smoothly, and takes the focus without a second jump
                target.scrollIntoView({ behavior: "smooth", block: "center" });
                target.focus({ preventScroll: true });
            } else {
                target.focus();
            }
        }
        // focus_count: the same first fault, checked again, is said again
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshot.focus_path, snapshot.focus_count, dom_form_id]);

    const lang = lang_of(given_lang || definition?.lang);
    const base_class_names = unstyled ? NO_CLASSES : (given_class_names || default_class_names);
    // the same names every time: `fb-field-email`, `fb-input`... a site styles from its own CSS (see styles.js)
    const class_names = useMemo(() => (stable_class_names ? { ...base_class_names, [STABLE_FLAG]: true } : base_class_names), [base_class_names, stable_class_names]);

    const render = useCallback(() => {
        if (loaded.loading) {
            return h("p", { role: "status" }, label_of("loading", lang, messages));
        }
        if (!definition?.version || !controller) {
            return null;
        }
        return h(FormView, {
            definition, snapshot, actions: controller, form_id: dom_form_id, lang, messages, styles, class_names, geo, custom_fields, field_components, max_file_bytes, price_unit,
            captcha_node: captcha && site_has_captcha && definition.captcha_enabled !== false ? h(CapCaptcha, { place: FORM_PLACE }) : null,
            needs_login: !!definition.requires_login && !is_logged_in,
            submit_label, success_content, title_tag, hide_title,
        });
    }, [loaded.loading, definition, controller, snapshot, dom_form_id, lang, messages, styles, class_names, geo, custom_fields, field_components, captcha, site_has_captcha, is_logged_in, max_file_bytes, price_unit, submit_label, success_content, title_tag, hide_title]);

    // The groups alone, for a form shown in steps: a step is some groups, by their keys, drawn one after the other in a box that
    // carries the form's look (the variables of the default CSS, styles.form.root) but is no <form>, has no title and no submit
    // button - the site has its own. Same fields, conditions and errors as render().
    const render_groups = useCallback((group_keys) => {
        if (!definition?.version || !controller) {
            return null;
        }
        const wanted = new Set(group_keys || []);
        return h(
            "div",
            { ...scope_props({ scope: "form", slot: "root", class_names, styles }), "data-form-key": definition.key, "data-form-id": definition.id, lang: definition.lang || undefined },
            ...groups_of(definition.version).filter((group) => wanted.has(group?.key)).map((group) => h(Group, {
                key: group.key, group, snapshot, actions: controller, form_id: dom_form_id, lang, messages, styles, class_names, geo, custom_fields, field_components, max_file_bytes, price_unit,
            })),
        );
    }, [definition, controller, snapshot, dom_form_id, lang, messages, styles, class_names, geo, custom_fields, field_components, max_file_bytes, price_unit]);

    // One instance of a group, for a site that draws the instances of a repeatable group itself (its own card, no add / remove):
    // the fields of that instance in the group's grid, in the same box as render_groups. `overrides.custom_fields` /
    // `overrides.field_components` win over the hook's for this instance only (a stepper whose maximum is this row's quantity).
    // Added for jument's return request; render_groups draws every instance with core's header and buttons.
    const render_instance = useCallback((group_key, index = 0, overrides = {}) => {
        if (!definition?.version || !controller) {
            return null;
        }
        const group = groups_of(definition.version).find((item) => item?.key === group_key);
        if (!group) {
            return null;
        }
        return h(
            "div",
            { ...scope_props({ scope: "form", slot: "root", class_names, styles }), "data-form-key": definition.key, "data-form-id": definition.id, lang: definition.lang || undefined },
            h(GroupInstance, {
                group, index, snapshot, actions: controller, form_id: dom_form_id, lang, messages, styles, class_names, geo, max_file_bytes, price_unit,
                custom_fields: overrides?.custom_fields ? { ...custom_fields, ...overrides.custom_fields } : custom_fields,
                field_components: overrides?.field_components ? { ...field_components, ...overrides.field_components } : field_components,
            }),
        );
    }, [definition, controller, snapshot, dom_form_id, lang, messages, styles, class_names, geo, custom_fields, field_components, max_file_bytes, price_unit]);

    return {
        render,
        render_groups,
        render_instance,
        // the values a new instance of a repeatable group starts with (its fields' defaults): for building the instances with load_values
        instance_defaults: (group_key) => group_defaults(groups_of(definition?.version).find((item) => item?.key === group_key)),
        // the captcha the form asks for (null when it does not), for a site that draws its own submit button
        captcha_node: captcha && site_has_captcha && definition?.captcha_enabled !== false ? h(CapCaptcha, { place: FORM_PLACE }) : null,
        definition,
        values: snapshot.values,
        errors: snapshot.errors,
        submitting: snapshot.submitting,
        submitted: snapshot.submitted,
        duplicate: snapshot.duplicate,
        // the text of the error that belongs to the form itself (a throttle, a captcha, a lost connection), for a site that draws its own
        form_error: first_error_text(snapshot.errors?._form, lang, messages),
        loading: loaded.loading,
        not_found: loaded.not_found,
        // for a site that drives the form itself: set_value(group_key, field_key, value, index), submit(), reset()
        set_value: (group_key, field_key, value, index = 0) => controller?.set_value(group_key, field_key, value, index),
        submit: () => controller?.submit(),
        reset: () => controller?.reset(),
        // for a form in steps: judge the groups of one step, hold a draft, say a mistake the form cannot know (a rule of the site's own field)
        validate_groups: (group_keys) => controller?.validate_groups(group_keys) ?? { status: "ok" },
        load_values: (next) => controller?.load_values(next),
        set_field_error: (path, codes) => controller?.set_field_error(path, codes),
    };
};

export default useFormRenderer;
export { useFormRenderer };
